import {
	BehaviorSubject,
	defer,
	defaultIfEmpty,
	distinctUntilChanged,
	EMPTY,
	filter,
	finalize,
	firstValueFrom,
	from,
	fromEvent,
	ignoreElements,
	map,
	merge,
	Observable,
	of,
	switchMap,
	take,
	tap,
} from 'rxjs'
import { ContextEvent as ContextRequestEvent } from '@lit/context'
import type { THistoryStrategy } from '@schmancy/router/router.types'
import { themeContext } from '@schmancy/theme/context'
import { theme as themeService } from '@schmancy/theme/theme.service'
import type { SchmancyThemeComponent } from '@schmancy/theme/theme.component'
import { findFillBoundary } from '@schmancy/directives/fill'
import { discoverComponent } from '@schmancy/mixins/discovery.service'
// Side-effect import, not just `import type` — SchmancyOverlay is used only
// as a type below (`as SchmancyOverlay`), so a type-only import elides at
// build time and never registers `<schmancy-overlay>`. That registration
// used to happen accidentally within discoverComponent's old 100ms window;
// now that discovery is synchronous, `document.createElement('schmancy-overlay')`
// runs immediately and needs the definition to already exist.
import './overlay.component'
import type { SchmancyOverlay } from './overlay.component'
// Static: the overlay module index re-exports this body publicly (and consumers
// mount `<schmancy-overlay-prompt-body>` by tag), so it always shares this chunk.
import { SchmancyOverlayPromptBody } from './overlay.confirm-body'
import {
	clearStack,
	currentStack,
	elementsByTag$,
	pushEntry,
	removeEntry,
	stack$ as internalStack$,
} from './overlay.stack'
import type {
	CloseReason,
	OverlayConfirmOptions,
	Content,
	OverlayEntry,
	OverlayPromptOptions,
	ResolvedShowOptions,
	ShowOptions,
} from './overlay.types'

/**
 * Public read-only stream of the current overlay stack. Subscribe to
 * observe stack changes — e.g. to update a breadcrumb, show a "close
 * all" button, or gate another action while any overlay is open.
 *
 * Emits synchronously with the current snapshot on subscribe. Never
 * completes — the caller owns teardown via `takeUntil(this.disconnecting)`.
 */
export const openOverlays$: Observable<readonly OverlayEntry[]> = internalStack$

/* ======================================================================= *
 *                        ambient event capture                              *
 * ======================================================================= *
 * The service listens to pointerdown / click / keydown at capture phase on the document
 * and remembers the most-recent user gesture. When show() is invoked
 * synchronously (or shortly after) in response to that gesture, the remembered
 * event becomes the anchor. This matches the "anchored is the novel
 * default" principle without forcing callers to thread events through
 * handlers.
 *
 * Staleness guard: an event older than AMBIENT_ANCHOR_MAX_AGE_MS is ignored.
 * Timer-driven or async show() calls that don't originate from a user gesture
 * fall through to centered / sheet layout.
 */
const AMBIENT_ANCHOR_MAX_AGE_MS = 750

interface AmbientAnchor {
	event: MouseEvent
	/** `composedPath()[0]`, read during dispatch — afterwards it returns `[]`,
	 *  and `event.target` alone is retargeted to the outermost host, losing the
	 *  shadow-tree location a boundary is resolved from. */
	origin: Element | null
	capturedAt: number
}

function deepestTarget(e: Event): Element | null {
	const first = e.composedPath()[0]
	return first instanceof Element ? first : e.target instanceof Element ? e.target : null
}

/** A gesture that carries no pointer position, anchored at `el`'s centre. */
function anchorAtCentre(el: Element): AmbientAnchor {
	const rect = el.getBoundingClientRect()
	const synthetic = new MouseEvent('click', {
		clientX: rect.left + rect.width / 2,
		clientY: rect.top + rect.height / 2,
		bubbles: true,
	})
	// Never dispatched, so the platform never sets `target` on it — and
	// `overlay.layout.ts`'s `resolveAnchorRef` reads `target` off this
	// event to build a live-tracking anchor. Without this override a
	// pointerless open silently degrades to a frozen point and never
	// re-tracks the activated element on scroll.
	Object.defineProperty(synthetic, 'target', { value: el, configurable: true })
	return { event: synthetic, origin: el, capturedAt: performance.now() }
}

/**
 * BehaviorSubject projected off three document-level event streams,
 * merged as Observables. A keydown synthesizes a MouseEvent at its
 * element's centre; a click that reports no position keeps the anchor its
 * own gesture recorded, or takes its element's centre when there is none, so
 * the anchor path carries keyboard- and script-triggered opens uniformly.
 *
 * The singleton subscribe has no explicit teardown — the module's
 * lifetime IS the subscription's lifetime.
 */
const ambientAnchor$ = new BehaviorSubject<AmbientAnchor | null>(null)

if (typeof document !== 'undefined') {
	const pointerdown$ = fromEvent<PointerEvent>(document, 'pointerdown', { capture: true, passive: true }).pipe(
		map((e): AmbientAnchor => ({ event: e, origin: deepestTarget(e), capturedAt: performance.now() })),
	)
	const click$ = fromEvent<Event>(document, 'click', { capture: true, passive: true }).pipe(
		map((e): AmbientAnchor | null => {
			const origin = deepestTarget(e)
			// A position is a pointer's (a pointerType, or a counted click) or a
			// script's own non-zero coordinates. The click a key press appends to
			// its keydown, a click a <label> forwards to its control, and a
			// script's `.click()` carry pointerType '' and detail 0, and their
			// clientX/Y are (0,0) in Chromium, the control's centre in WebKit's
			// keyboard click, and the label's pointer in WebKit's forwarded one —
			// never something to read as where the gesture landed. A component
			// re-announcing its own click as `new CustomEvent('click', { detail })`
			// carries no clientX/Y at all, and `undefined !== 0` would read that
			// absence as a position.
			if (
				e instanceof MouseEvent &&
				((typeof PointerEvent !== 'undefined' && e instanceof PointerEvent && e.pointerType !== '') ||
					e.detail > 0 ||
					(!e.isTrusted && (e.clientX !== 0 || e.clientY !== 0)))
			) {
				return { event: e, origin, capturedAt: performance.now() }
			}
			if (!origin) return null
			// The gesture that produced this click already recorded its anchor —
			// the keydown on this control, the pointer on the label that names it,
			// or the pointer or key inside the component re-announcing it — so that
			// one stands, and returning null leaves it in place.
			const prior = freshAmbient()
			if (prior?.origin?.closest('label')?.control === origin) return null
			for (let node: Node | null = prior?.origin ?? null; node; ) {
				if (node === origin) return null
				node = node instanceof ShadowRoot ? node.host : node.parentNode
			}
			return anchorAtCentre(origin)
		}),
		filter((ambient): ambient is AmbientAnchor => ambient !== null),
	)
	const keydown$ = fromEvent<KeyboardEvent>(document, 'keydown', { capture: true }).pipe(
		map((e): AmbientAnchor | null => {
			// The focused control, read once and used for every question this event
			// answers. `e.target` is NOT that control: this listener is on the
			// document, and a composed event's target is retargeted to the outermost
			// host by the time it is read here — so a control inside a shadow tree
			// reports its whole host, and a rect synthesized off that host puts the
			// point at the host's centre. A 48px mark at the head of an 80px-wide,
			// full-height nav column anchors ~370px below itself. A real pointer
			// carries its own coordinates and has nothing equivalent to lose; this
			// leg has to compute its point, so reading the wrong element is the
			// whole answer being wrong.
			const focused = deepestTarget(e)
			return focused ? anchorAtCentre(focused) : null
		}),
		filter((ambient): ambient is AmbientAnchor => ambient !== null),
	)

	merge(pointerdown$, click$, keydown$).subscribe(ambient => ambientAnchor$.next(ambient))
}

function freshAmbient(): AmbientAnchor | undefined {
	const cur = ambientAnchor$.value
	if (!cur) return undefined
	if (cur.origin && !cur.origin.isConnected) return undefined
	if (performance.now() - cur.capturedAt > AMBIENT_ANCHOR_MAX_AGE_MS) return undefined
	return cur
}

/* ======================================================================= *
 *                          history entry ledger                             *
 * ======================================================================= *
 * The ordered record of the session-history entries overlays have pushed —
 * the only place that knows which entry belongs to which overlay and whether
 * that overlay is still open. Both history questions are answered from it and
 * never from the id sitting on the current entry, which identifies the
 * top-most overlay alone and says nothing about the ones beneath it: an
 * overlay closes on a traversal only when its OWN entry is among the ones the
 * traversal took, and a teardown hands an entry back only when that entry is
 * the current one.
 *
 * Assumes overlay entries sit contiguously at the top of session history. An
 * entry the router or the host app pushes between two of them carries nothing
 * this ledger can read, so a traversal landing there reads as landing below
 * every overlay entry. Every overlay entry also carries a sequence that grows
 * with each push, so an entry the ledger no longer holds — a closed overlay's,
 * reached by Forward — still reads as above the ledger, not below it.
 */

interface HistoryLedgerEntry {
	id: string
	href: string
	areas: string | undefined
	/** Cleared when the overlay tears down. Session history can only drop its
	 *  current entry, so a dead entry under a live one stays here as a
	 *  tombstone until the entries above it are gone. */
	live: boolean
	/** Runs when a traversal takes this entry out of session history; false when
	 *  the overlay refused to close, which puts the entry back. */
	popped: () => boolean
	/** Also stamped on the entry's `history.state`; grows with every push, so it
	 *  orders entries the way session history does. */
	seq: number
}

const historyLedger: HistoryLedgerEntry[] = []

/** A `history.back()` this ledger asked for has not landed yet. */
let handingBack = false

/** Entries asked for while a traversal is on its way, pushed once it lands. */
const waitingToEnter: Array<{ entry: Omit<HistoryLedgerEntry, 'seq' | 'href' | 'areas'>; schmancyAreas: unknown }> = []

/** The last sequence handed out. Sequences are wall-clock milliseconds nudged
 *  past it, so they keep growing across a reload: an entry an earlier page life
 *  left behind still sorts below every entry pushed since. */
let historySeq = 0

/** Pushes a session-history entry for `entry`, stamped with the next sequence,
 *  and records it on top of the ledger. `schmancyAreas` rides along unchanged —
 *  the router's own record of which component is mounted in which area, an
 *  address-level key this entry inherits rather than authors. */
function enterHistory(entry: Omit<HistoryLedgerEntry, 'seq' | 'href' | 'areas'>, schmancyAreas?: unknown): void {
	// A traversal runs later, from whatever entry is current then, so an entry pushed
	// before it lands would be the one it takes.
	if (handingBack) {
		waitingToEnter.push({ entry, schmancyAreas })
		return
	}
	historySeq = Math.max(historySeq + 1, Date.now())
	history.pushState({ schmancyAreas, schmancyOverlayId: entry.id, schmancyOverlaySeq: historySeq }, '', location.href)
	historyLedger.push({ ...entry, seq: historySeq, href: location.href, areas: JSON.stringify(schmancyAreas) })
}

/**
 * Hands back the ledger's top entry once its overlay is gone — one step per
 * traversal, and only after `history.state` confirms that entry is the one
 * `history.back()` would actually take. Several overlays unwound by a single
 * teardown therefore walk down one verified step at a time instead of racing
 * unverified `back()` calls against each other.
 */
function releaseDeadTopEntry(): void {
	const top = historyLedger[historyLedger.length - 1]
	if (!top || top.live) return
	if (history.state?.schmancyOverlayId !== top.id) return
	historyLedger.pop()
	// A route replacement may retain our id while claiming the entry for a new
	// destination. Releasing that entry with Back would undo the user's navigation.
	if (location.href !== top.href || JSON.stringify(history.state?.schmancyAreas) !== top.areas) {
		const state = { ...history.state }
		delete state.schmancyOverlayId
		delete state.schmancyOverlaySeq
		history.replaceState(state, '', location.href)
		return
	}
	handingBack = true
	history.back()
}

if (typeof window !== 'undefined') {
	fromEvent<PopStateEvent>(window, 'popstate').subscribe(() => {
		handingBack = false
		// Every ledger entry pushed after the landing one has left session history.
		// An entry carrying no sequence sits below them all; a closed overlay's
		// leftover that Forward reaches sits above them all and takes nothing.
		const landingSeq =
			typeof history.state?.schmancyOverlaySeq === 'number' ? history.state.schmancyOverlaySeq : -Infinity
		let landed = historyLedger.length
		while (landed > 0 && historyLedger[landed - 1].seq > landingSeq) landed--
		const taken = historyLedger.splice(landed)
		// Asked top-down, the order the surfaces sit in. The first to refuse keeps
		// its entry and every one beneath it, unasked: one confirmation at a time,
		// and no overlay closes under one that stays.
		let kept = taken.length
		while (kept > 0 && (!taken[kept - 1].live || taken[kept - 1].popped())) kept--
		for (const entry of taken.slice(0, kept)) {
			if (entry.live) enterHistory(entry)
		}
		// The traversal may have just exposed a tombstone.
		releaseDeadTopEntry()
		for (const { entry, schmancyAreas } of waitingToEnter.splice(0)) {
			if (entry.live) enterHistory(entry, schmancyAreas)
		}
	})
}

/* ======================================================================= *
 *                                   show                                    *
 * ======================================================================= */

/**
 * Open an overlay containing `content`. Returns a cold Observable — the
 * overlay mounts on subscribe and dismisses on unsubscribe. Emits at
 * most one value (the result from content's `close` event) then completes.
 *
 * The subscription IS the overlay lifecycle. `takeUntil(this.disconnecting)`
 * on the caller's side means the overlay auto-dismisses when the caller
 * unmounts — no handles to track, no leaks.
 *
 * **The anchor is the tracked gesture, and there is no other.**
 * The service tracks the most-recent user gesture at the document level and
 * derives the anchor from it, so a `show()` invoked from a `@click` handler
 * already blooms from the point of attention — from the control the user
 * actually touched, re-read on every positioning pass. No option can move it
 * elsewhere, so call `show()` synchronously from the gesture handler, or capture
 * it with `captureShow()` before asynchronous work. A
 * `show()` with no fresh gesture in the window (a timer, a stream, a state
 * change) resolves to the sheet / centered layout, which is the right answer
 * for a surface no gesture opened: there is no point of attention to bloom
 * from (see overlay.layout.ts).
 *
 * Content the gesture does NOT own — an outcome, a refusal, anything the user
 * has to read before moving on — says so with `{ sticky: true }`: announced
 * as a `dialog` and no longer dismissed by the next click anywhere. It still
 * anchors to the gesture that opened it; `sticky` answers what the content
 * is, never where it goes. Always pair it with a close control the content
 * renders — a sticky surface with nothing focusable inside leaves only
 * Escape to close it.
 *
 * @example
 * show(MyEditor, { props: { id } })
 *   .pipe(takeUntil(this.disconnecting))
 *   .subscribe(saved => saved && this.store.persist(saved))
 */
export function show<T = void>(content: Content, options: ShowOptions = {}): Observable<T | undefined> {
	return open<T>(content, options)
}

/** Capture the current gesture before asynchronous work, then open from it when ready. */
export function captureShow(): typeof show {
	const ambient = freshAmbient()
	const origin = ambient?.origin?.closest('button, a, [role="button"]') ?? ambient?.origin
	let captured: AmbientAnchor | null = null
	if (ambient && origin) {
		const event = new MouseEvent('click', { clientX: ambient.event.clientX, clientY: ambient.event.clientY })
		Object.defineProperty(event, 'target', { value: origin })
		captured = { event, origin, capturedAt: ambient.capturedAt }
	}
	return <T = void>(content: Content, options: ShowOptions = {}) => open<T>(content, options, undefined, captured)
}

/** Open within a palette's own scope, including calls without a current gesture.
 * The gesture still owns the anchor; the provider's ancestry owns containment. */
export function showInTheme<T = void>(
	theme: SchmancyThemeComponent,
	content: Content,
	options: ShowOptions = {},
): Observable<T | undefined> {
	return open<T>(content, options, theme)
}

function open<T>(
	content: Content,
	options: ShowOptions,
	scope?: SchmancyThemeComponent,
	captured?: AmbientAnchor | null,
): Observable<T | undefined> {
	// In-package components with a resolved non-gesture anchor — a docked boat
	// corner — build the internal `ResolvedShowOptions` shape. `ShowOptions`
	// carries no anchor, so for every consumer the ambient gesture is it.
	const internal = options as ResolvedShowOptions
	return defer(() => {
		// Resolve anchor + theme container at subscribe time. Theme: an ancestor
		// climb from the same origin the boundary climbs from, so the overlay
		// mounts inside the nearest <schmancy-theme> ABOVE the gesture that
		// triggered it and inherits its --schmancy-* tokens. Falls back to body
		// when no theme is found (no origin, or no <schmancy-theme> ancestor).
		//
		// One snapshot for both reads: a second call could land on a newer gesture,
		// pairing this overlay's anchor with an unrelated one's boundary.
		const ambient = captured === undefined ? freshAmbient() : captured?.origin?.isConnected ? captured : undefined
		const origin =
			internal.trigger ?? (internal.anchor instanceof Element ? internal.anchor : (ambient?.origin ?? null))
		// The caller's own answer first; otherwise the frame the gesture came from.
		// `findFillBoundary` is `fill()`'s own climb — one `[data-fill-boundary]`
		// marker answers "which box is the viewport here" for both primitives, so a
		// frame declares itself once and every view and overlay inside it obeys.
		//
		// EXCEPT A VIEWER, which obeys no frame. It is the thing being looked at and
		// it owns the screen; a picture confined to the box it was opened from is the
		// one case where the layout around it can still obscure it. This is not a
		// z-order problem and no z-index answers it: a bounded overlay is MOUNTED
		// INSIDE the frame, so the frame's own overflow clips it, and the top-layer
		// promotion — the only thing that renders outside the whole ancestor chain,
		// immune to every ancestor overflow, transform, filter and stacking context —
		// is deliberately skipped for it. Refusing the boundary here lifts the picture
		// clear of its container structurally, rather than trying to out-stack it.
		const boundary = internal.layout === 'viewer' ? null : (internal.boundary ?? findFillBoundary(scope ?? origin))
		const resolvedOptions: ResolvedShowOptions = {
			...internal,
			anchor: internal.anchor ?? ambient?.event,
			boundary: boundary ?? undefined,
			// The gesture's own element, carried through rather than dropped after the
			// theme and boundary climbs above. `anchor` is a MouseEvent for every
			// ambient open — a point with no extent — so an entrance built from it can
			// only scale about that point. A surface that grows out of the thing it was
			// opened from needs that thing's box.
			trigger: internal.trigger ?? (origin instanceof HTMLElement ? origin : undefined),
		}
		const id = generateId()
		const state = { settled: false }

		return (scope ? of(scope) : discoverComponent<HTMLElement>('schmancy-theme', origin)).pipe(
			switchMap(theme => {
				const el = document.createElement('schmancy-overlay') as SchmancyOverlay
				// Mounting inside the boundary is what makes containment possible: the
				// frame's own position/overflow then bound and clip the overlay, and
				// the focus trap's sibling sweep scopes to the frame instead of
				// blanking the host page. Set before the append, so the first render
				// already carries the attribute the containment styles key on.
				el.bounded = boundary !== null
				// The overlay is `position:absolute; inset:0` inside the frame, so the
				// frame has to be the containing block that resolves against — a frame
				// still on `position: static` would otherwise resolve the overlay
				// against the page and let it escape the box it was mounted in. A static
				// box with no positioned children renders identically as relative, and
				// declaring itself a boundary is what licenses the write.
				if (boundary && getComputedStyle(boundary).position === 'static') boundary.style.position = 'relative'
				// Mounted under no theme, the content's request for its locale reaches no
				// provider and falls back to the browser's, so a date in a dialog reads
				// differently from the page that opened it. Such requests are re-asked of
				// the root theme. Subscribed before the append, so the overlay's own
				// connect-time request is answered too.
				const localeSource = theme ? null : themeService.rootComponent
				const localeForward = localeSource
					? fromEvent(el, 'context-request')
							.pipe(
								filter(
									(event): event is ContextRequestEvent<typeof themeContext> =>
										event instanceof ContextRequestEvent && event.context === themeContext,
								),
							)
							.subscribe(event => {
								event.stopPropagation()
								localeSource.dispatchEvent(
									new ContextRequestEvent(themeContext, event.contextTarget, event.callback, event.subscribe),
								)
							})
					: null
				;(scope ?? boundary ?? theme ?? document.body ?? document.documentElement).appendChild(el)

				const opened$ = from(
					el.updateComplete.then(() =>
						el.open(content, resolvedOptions, () =>
							pushEntry({
								id,
								element: el,
								layout: el.layout,
								modal: el.modal,
								tier: el.tier,
							}),
						),
					),
				).pipe(
					tap(() => {
						const strategy: THistoryStrategy = resolvedOptions.historyStrategy ?? 'push'
						// A session-history entry is co-owned, and each writer preserves the keys it
						// did not author. Both writes below pass `location.href` through unchanged, so
						// the entry each produces stands at the very same address as the one already
						// there — and `schmancyAreas`, the router's record of which component is
						// mounted in which area, is that address's own description. Dropping it leaves
						// the router accumulating its map from an empty bag, so the next route push
						// writes an address naming only the area it just pushed and erases every other
						// mounted area from the URL and from session history.
						const currentState = history.state ?? {}
						if (strategy === 'push') {
							// A push mints an entry no other owner created, so `enterHistory` NAMES what
							// it carries rather than spreading the bag: an unknown foreign key may be
							// scoped to the entry rather than to the address — a host app's own `my panel
							// is open` marker — and carrying one of those onto a new entry would make it
							// impersonate the one beneath it. `schmancyAreas` is the one address-level key
							// this library knows about and threads through by name; a future key that
							// likewise describes the ADDRESS has to be added here by hand. Only a pushed
							// entry joins the ledger — `replace` creates no entry of its own, so it has
							// none to hand back and none a traversal can take.
							enterHistory(
								{
									id,
									live: true,
									popped: () => {
										if (!el.allowsDismiss('popstate')) return false
										state.settled = true
										void el.close('popstate')
										return true
									},
								},
								currentState.schmancyAreas,
							)
						} else if (strategy === 'replace') {
							// The current entry stays the same entry, so every key already on it — the
							// router's schmancyAreas, an overlay sequence — keeps its owner, and only this
							// service's own id is overwritten.
							history.replaceState({ ...currentState, schmancyOverlayId: id }, '', location.href)
						}
					}),
				)

				const closed$ = el.closed$.pipe(
					take(1),
					tap(() => {
						state.settled = true
					}),
					map(({ result }) => result as T | undefined),
				)

				// `wireCloseTriggers` (inside `open()`) is armed before the entrance
				// animation resolves, so a close can complete `el.closed$` before
				// `opened$` emits — gating the close listen behind `opened$` would
				// subscribe to an already-completed Subject and drop it forever.
				// `opened$`'s side effects run via `ignoreElements()` so they never
				// gate the outer `take(1)`, which resolves on the real close alone.
				return merge(opened$.pipe(ignoreElements()), closed$).pipe(
					take(1),
					finalize(() => {
						// The element leaves the DOM on the next microtask, not after the exit
						// animation `close()` awaits: a consumer that finds its panel by
						// querying the document — `document.querySelectorAll('schmancy-overlay')`
						// — reads a still-present closing overlay as the live one, so holding
						// the element back to let it animate hands that query a corpse. A
						// programmatic teardown therefore forgoes the exit animation; a close
						// the user drove plays it, because `close()` finishes before the
						// subscription this finalize belongs to ever unwinds.
						if (!state.settled) void el.close('programmatic')
						queueMicrotask(() => el.remove())
						localeForward?.unsubscribe()
						removeEntry(id)
						const waiting = waitingToEnter.find(({ entry }) => entry.id === id)
						if (waiting) waiting.entry.live = false
						const entry = historyLedger.find(e => e.id === id)
						if (entry) {
							entry.live = false
							releaseDeadTopEntry()
						}
					}),
				)
			}),
		)
	})
}

/* ======================================================================= *
 *                            confirm / prompt sugar                         *
 * ======================================================================= */

/**
 * Yes/no confirmation dialog. Returns a Promise that resolves with the
 * user's choice. `variant: 'danger'` flips to destructive styling.
 */
export async function confirm(options: OverlayConfirmOptions = {}): Promise<boolean> {
	const result = await firstValueFrom(
		show<boolean>(SchmancyOverlayPromptBody, {
			// A question whose answer the caller is awaiting: a dismiss resolves this
			// promise `false`, so the next click anywhere must not be able to answer
			// it. The question still belongs beside the button that asked it. Its own
			// Confirm/Cancel buttons are the close control `sticky` requires.
			sticky: true,
			ariaLabel: options.title ?? 'Confirmation',
			signal: options.signal,
			props: {
				mode: 'confirm',
				heading: options.title,
				subtitle: options.subtitle,
				message: options.message,
				confirmText: options.confirmText ?? 'Confirm',
				cancelText: options.cancelText ?? 'Cancel',
				variant: options.variant ?? 'default',
			},
		}).pipe(defaultIfEmpty(false as boolean | undefined)),
	)
	return result === true
}

/**
 * Input prompt dialog. Returns the entered string, or `null` if the user
 * cancels or dismisses.
 */
export async function prompt(options: OverlayPromptOptions = {}): Promise<string | null> {
	const result = await firstValueFrom(
		show<string | null>(SchmancyOverlayPromptBody, {
			// Same contract as confirm(): a dismiss resolves `null`, so a stray click
			// must not be able to cancel the entry. Its own OK/Cancel buttons are the
			// close control `sticky` requires.
			sticky: true,
			ariaLabel: options.title ?? options.label ?? 'Prompt',
			signal: options.signal,
			props: {
				mode: 'prompt',
				heading: options.title,
				subtitle: options.subtitle,
				message: options.message,
				label: options.label,
				defaultValue: options.defaultValue ?? '',
				placeholder: options.placeholder,
				inputType: options.inputType ?? 'text',
				pattern: options.pattern,
				required: options.required ?? false,
				confirmText: options.confirmText ?? 'OK',
				cancelText: options.cancelText ?? 'Cancel',
			},
		}).pipe(defaultIfEmpty(null as string | null | undefined)),
	)
	return typeof result === 'string' ? result : null
}

/* ======================================================================= *
 *                              overlayEvents                                *
 * ======================================================================= */

/**
 * Subscribe to custom events emitted from any currently-open overlay
 * whose content matches `tagName`. Inspired by `area.on(name)` — keyed
 * by tag name (stable across HMR / lazy chunks) rather than a uid or
 * class reference.
 *
 * The returned Observable never completes on its own — the caller owns
 * teardown via `takeUntil(this.disconnecting)`. During gaps where no
 * matching overlay is open, no events are emitted; when an instance
 * mounts, events flow.
 *
 * Stacked instances of the same content merge their event streams.
 */
export function overlayEvents<E extends Event = CustomEvent>(tagName: string, eventName: string): Observable<E> {
	return elementsByTag$(tagName).pipe(
		distinctUntilChanged((a, b) => a.length === b.length && a.every((el, i) => el === b[i])),
		switchMap(elements => (elements.length === 0 ? EMPTY : merge(...elements.map(el => fromEvent<E>(el, eventName))))),
		map(e => e),
	)
}

/* ======================================================================= *
 *                                dismissAll                                 *
 * ======================================================================= */

/**
 * Close every currently-open overlay. LIFO order. Imperative — use for
 * app-level flows like logout or route reset.
 */
export function dismissAll(): void {
	const stack = [...currentStack()]
	// LIFO: close top-of-stack first.
	for (let i = stack.length - 1; i >= 0; i--) {
		const entry = stack[i]
		const overlay = entry.element as SchmancyOverlay
		void overlay.close('programmatic')
	}
	clearStack()
}

/* ======================================================================= *
 *                                 helpers                                   *
 * ======================================================================= */

function generateId(): string {
	// 8-char base36 is enough entropy for session-scoped uniqueness.
	return 'ov_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
}

// Re-export the close reason type for consumers who want to narrow on it.
export type { CloseReason }
